import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/query-client";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CustomerProvider } from "@/contexts/CustomerContext";
import { ProductProvider } from "@/contexts/ProductContext";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import TrialExpired from "@/pages/TrialExpired";
import SystemAdmin from "@/pages/SystemAdmin";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import NewCustomer from "@/pages/NewCustomer";
import FollowUps from "@/pages/FollowUps";
import RoomRebuild from "@/pages/RoomRebuild";
import CreatePost from "@/pages/CreatePost";
import FredChat from "@/pages/FredChat";
import Products from "@/pages/Products";
import Messages from "@/pages/Messages";
import Settings from "@/pages/Settings";
import Team from "@/pages/Team";
import Translator from "@/pages/Translator";
import Training from "@/pages/Training";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, trialStatus } = useAuth();
  if (isLoading) return <div className="loading-spinner" style={{ height: "100vh" }} />;
  if (!isAuthenticated) return <Navigate to="/" replace />;
  if (trialStatus.isExpired) return <Navigate to="/trial-expired" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  const { isAuthenticated, trialStatus, user } = useAuth();
  const isSysAdmin = user?.isSysAdmin;

  return (
    <Routes>
      <Route path="/" element={isAuthenticated ? (isSysAdmin ? <Navigate to="/system-admin" replace /> : <Navigate to="/dashboard" replace />) : <Landing />} />
      <Route path="/login" element={isAuthenticated ? (isSysAdmin ? <Navigate to="/system-admin" replace /> : <Navigate to="/dashboard" replace />) : <Login />} />
      <Route path="/register" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Register />} />
      <Route path="/forgot-password" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPassword />} />
      <Route path="/trial-expired" element={
        isAuthenticated && trialStatus.isExpired ? <TrialExpired /> : <Navigate to="/" replace />
      } />
      <Route path="/system-admin" element={
        isAuthenticated && isSysAdmin ? <SystemAdmin /> : <Navigate to="/" replace />
      } />
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/customers/new" element={<NewCustomer />} />
        <Route path="/customers/:id" element={<CustomerDetail />} />
        <Route path="/follow-ups" element={<FollowUps />} />
        <Route path="/room-rebuild" element={<RoomRebuild />} />
        <Route path="/create-post" element={<CreatePost />} />
        <Route path="/fred" element={<FredChat />} />
        <Route path="/products" element={<Products />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/translator" element={<Translator />} />
        <Route path="/training" element={<Training />} />
        <Route path="/team" element={<Team />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CustomerProvider>
          <ProductProvider>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </ProductProvider>
        </CustomerProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
